// Tailor's baked residual-GP field — client-side N-linear slicing (FXC-9009).
//
// The backend bakes the residual-GP once over the axes that GENUINELY vary and
// ships the whole N-D field as a flat C-order array (`GET /composite/gp-field`).
// The browser slices it locally so dragging a held-value slider reslices the
// surface in real time WITHOUT a backend round-trip (no ~14s surface_grid
// assembly per move). This is the "I want to see Tailor, sliced live" path:
// the smooth residual-GP surface, not the raw-binned SurfaceCorrelogram grid.
//
// Contract of the payload — see thread-api `routes/composite_db.py:get_gp_field`:
//   * `axes` is the grid axis order (matches `shape`, matches the flat stride
//     of `mean`/`sigma`/`prior`);
//   * `axis_values[axis]` are the grid NODES along that axis (ascending);
//   * `mean`/`sigma`/`prior` are flat C-order over `shape`, length ∏shape;
//   * `axes_varying` is the subset the client can slide (cardinality > 1). The
//     rest (geometry on beecbb20) collapse to cardinality 1 — no interpolation.
//
// This module is PURE (no React, no three) so it unit-tests without a DOM.

export interface GpFieldAvailable {
  available: true;
  quantity: string;
  /** Grid axis order — matches `shape` and the flat stride of `mean`/`sigma`/`prior`. */
  axes: string[];
  /** Grid nodes per axis (ascending). Keyed by axis id; not necessarily in grid order. */
  axis_values: Record<string, number[]>;
  /** The axes the client can slide in real time (cardinality > 1), in grid order. */
  axes_varying: string[];
  /** Axes genuinely varying in the data but PINNED by the bake (single grid
   *  node at their densest data value) — the top-k grid budget went to the
   *  densely-swept axes (FXC-9009). Absent on pre-fix bakes. */
  held_axes?: Record<string, number> | null;
  /** Most-populated data value per axis — seeds the default-held sliders on
   *  evidence instead of the median grid node (which can land in zero-data
   *  corners of a fill campaign). Absent on pre-fix bakes. */
  axis_densest?: Record<string, number> | null;
  /** Cardinality per axis, in `axes` order. ∏shape === mean.length. */
  shape: number[];
  /** Flat C-order field mean, length ∏shape. */
  mean: (number | null)[];
  /** Flat C-order field sigma, or null when the bake carried no uncertainty. */
  sigma: (number | null)[] | null;
  /** Flat C-order ROM prior (z = prior + residual), or null. */
  prior: (number | null)[] | null;
  /** Honest engine label, e.g. "residual-gp". */
  engine: string | null;
  /** Fraction of the HF variance the prior already explains. */
  prior_share: number | null;
  /** How many HF residual points the GP was fit on. */
  n_residual: number | null;
  /** Distinct sampled HF conditions in the LIVE data — with n_residual this
   *  states the honest training cap: "GP trained on 500 of 3,882 solves". */
  hf_solves?: number | null;
  /** condition_keys of the rows that ACTUALLY trained the GP (post the
   *  pipeline's fit-point subsample). Absent on fields baked before the
   *  trained-keys capture — the UI then hides the training highlight. */
  trained_condition_keys?: string[] | null;
  /** Content fingerprint of the baked field. */
  fingerprint: string | null;
  /** True when the field no longer matches the current live datasets. */
  stale: boolean;
}

export interface GpFieldUnavailable {
  available: false;
  quantity: string;
}

export type GpField = GpFieldAvailable | GpFieldUnavailable;

export interface AxisDomain {
  axis: string;
  values: number[];
  min: number;
  max: number;
  /** True when the field can slice over this axis (cardinality > 1). */
  varying: boolean;
}

export interface SliceRequest {
  axisX: string;
  axisY: string;
  /** Output resolution across axisX (default: axisX cardinality, capped at 40). */
  nx?: number;
  /** Output resolution across axisY (default: axisY cardinality, capped at 40). */
  ny?: number;
  /** Held value for every axis other than axisX/axisY. Missing → axis midpoint. */
  held?: Record<string, number>;
  /** Which baked array to slice: the residual-GP `mean` (default) or the ROM
   *  `prior` — so the flexfoil prior can be shown against the corrected surface.
   *  Falls back to `mean` when the field carries no prior. */
  source?: "mean" | "prior";
}

export interface SliceResult {
  xAxis: string;
  yAxis: string;
  xVals: number[];
  yVals: number[];
  /** Row-major (y outer, x inner), length nx*ny — matches trust-map/surface ordering. */
  z: (number | null)[];
  /** Row-major sigma, or all-null when the field carries no sigma. */
  sigma: (number | null)[];
  nx: number;
  ny: number;
  /** Interpolated min/max over the finite z cells (for colour scaling). */
  zMin: number;
  zMax: number;
}

const MAX_SLICE = 40;

function clamp(value: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, value));
}

function axisMin(values: number[]): number {
  return values.reduce((m, v) => (v < m ? v : m), values[0] ?? 0);
}
function axisMax(values: number[]): number {
  return values.reduce((m, v) => (v > m ? v : m), values[0] ?? 0);
}

/** Per-axis domains for the field, in grid order. */
export function fieldAxisDomains(field: GpFieldAvailable): AxisDomain[] {
  const varying = new Set(field.axes_varying);
  return field.axes.map((axis) => {
    const values = field.axis_values[axis] ?? [];
    return {
      axis,
      values,
      min: values.length ? axisMin(values) : 0,
      max: values.length ? axisMax(values) : 0,
      varying: varying.has(axis) && values.length > 1,
    };
  });
}

/** Midpoint (median) node of an axis — the default hold.
 *  Memoized per values-array identity: the evidence transport calls this per
 *  row × per axis × per correlogram cell on continuous-valued campaign data,
 *  and re-sorting a copy every call was a profiled hotspot (FXC-9009). */
const axisMidpointCache = new WeakMap<number[], number>();
export function axisMidpoint(values: number[]): number {
  if (!values.length) return 0;
  const hit = axisMidpointCache.get(values);
  if (hit !== undefined) return hit;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted[Math.floor((sorted.length - 1) / 2)]!;
  axisMidpointCache.set(values, mid);
  return mid;
}

/**
 * C-order strides for `shape`: stride[i] = ∏ shape[j] for j > i.
 * A flat index is Σ coord[i] * stride[i].
 */
function cOrderStrides(shape: number[]): number[] {
  const strides = new Array<number>(shape.length);
  let acc = 1;
  for (let i = shape.length - 1; i >= 0; i -= 1) {
    strides[i] = acc;
    acc *= shape[i]!;
  }
  return strides;
}

/**
 * Locate a value inside an ascending grid: return the lower node index `i0`
 * and the fractional blend `t` so value ≈ values[i0]*(1-t) + values[i0+1]*t.
 * A value on (or clamped to) a node returns t exactly 0 or 1 at that node.
 */
function bracket(values: number[], value: number): { i0: number; i1: number; t: number } {
  const n = values.length;
  if (n <= 1) return { i0: 0, i1: 0, t: 0 };
  const lo = values[0]!;
  const hi = values[n - 1]!;
  const clamped = clamp(value, lo, hi);
  // ascending scan — grids here are tiny (≤27), so linear is fine and robust to
  // non-uniform spacing (mach 0.15/0.2/0.25, flap 0/10/30/40).
  for (let i = 0; i < n - 1; i += 1) {
    const a = values[i]!;
    const b = values[i + 1]!;
    if (clamped >= a && clamped <= b) {
      const span = b - a;
      const t = span > 0 ? (clamped - a) / span : 0;
      return { i0: i, i1: i + 1, t };
    }
  }
  // fell through (shouldn't after clamp) — pin to the top node
  return { i0: n - 1, i1: n - 1, t: 0 };
}

/** One contributing grid node along an axis, with its interpolation weight. */
type NodeWeight = { idx: number; w: number };

/** Contributing node list for a bracket: 1 node on a grid node, 2 between. */
function nodesForBracket(br: { i0: number; i1: number; t: number }): NodeWeight[] {
  if (br.i0 === br.i1 || br.t <= 0) return [{ idx: br.i0, w: 1 }];
  if (br.t >= 1) return [{ idx: br.i1, w: 1 }];
  return [{ idx: br.i0, w: 1 - br.t }, { idx: br.i1, w: br.t }];
}

/**
 * Walk the cartesian product of per-axis contributing nodes (≤ 2^k corners of
 * the interpolation hypercube) and blend the flat C-order array's values.
 * Renormalises over the finite corners when a ragged edge contributes nulls.
 * Shared by sliceGpField (per output cell) and evalGpField (one condition).
 */
function interpCorners(
  arr: (number | null)[],
  perAxis: NodeWeight[][],
  strides: number[],
): number | null {
  // Compact the walk (FXC-9009 perf): axes contributing a SINGLE node (held on
  // a grid node, cardinality-1 geometry axes) fold into a fixed base offset +
  // weight instead of a cursor dimension — the corner walk then only spans the
  // axes strictly between nodes. A slice eval with two swept axes drops from
  // 2^k corners × k-dim inner loops to 4 corners × 2-dim.
  let baseFlat = 0;
  let baseW = 1;
  const active: NodeWeight[][] = [];
  const activeStrides: number[] = [];
  for (let a = 0; a < perAxis.length; a += 1) {
    const nodes = perAxis[a]!;
    if (nodes.length === 1) {
      baseFlat += nodes[0]!.idx * strides[a]!;
      baseW *= nodes[0]!.w;
    } else {
      active.push(nodes);
      activeStrides.push(strides[a]!);
    }
  }
  if (baseW <= 0) return null;
  const dim = active.length;
  if (dim === 0) {
    const cell = arr[baseFlat];
    return typeof cell === "number" && Number.isFinite(cell) ? cell : null;
  }
  const cursor = new Array<number>(dim).fill(0);
  let acc = 0;
  let weightSeen = 0;
  let anyNull = false;
  for (;;) {
    let flat = baseFlat;
    let w = baseW;
    for (let a = 0; a < dim; a += 1) {
      const node = active[a]![cursor[a]!]!;
      flat += node.idx * activeStrides[a]!;
      w *= node.w;
    }
    if (w > 0) {
      const cell = arr[flat];
      if (typeof cell === "number" && Number.isFinite(cell)) {
        acc += cell * w;
        weightSeen += w;
      } else {
        anyNull = true;
      }
    }
    // increment mixed-radix cursor
    let a = dim - 1;
    for (; a >= 0; a -= 1) {
      const next = (cursor[a] ?? 0) + 1;
      cursor[a] = next;
      if (next < active[a]!.length) break;
      cursor[a] = 0;
    }
    if (a < 0) break;
  }
  if (weightSeen <= 0) return null;
  return anyNull ? acc / weightSeen : acc;
}

interface AxisPlan {
  /** Corner index pair + blend for a fixed (held) axis, or null for a swept axis. */
  fixed: { i0: number; i1: number; t: number } | null;
  /** Swept axis marker: "x" or "y" — its coord comes from the sweep loop. */
  sweep: "x" | "y" | null;
  cardinality: number;
}

/**
 * Slice the baked GP field over (axisX, axisY) into an nx×ny z-grid by N-linear
 * interpolation of the flat C-order `mean` (and `sigma`) over `axis_values`.
 * Every other axis is held at `held[axis]` (clamped to its [min,max], default
 * midpoint). Cardinality-1 axes contribute no interpolation.
 *
 * Interp walks the ≤2^k corners of the hypercube spanned by the axes that are
 * strictly between grid nodes (the two swept axes each contribute a corner bit
 * per output cell; held axes contribute their bits once). k is small, so this
 * is trivially fast (a 40×40 slice over a 3240-cell field reslices in <1ms).
 */
export function sliceGpField(field: GpFieldAvailable, req: SliceRequest): SliceResult {
  const { axes, shape, axis_values, mean, sigma } = field;
  // which baked array the z-grid samples: mean (default) or the ROM prior
  const zSource = req.source === "prior" && field.prior ? field.prior : mean;
  const strides = cOrderStrides(shape);
  const held = req.held ?? {};

  const xValues = axis_values[req.axisX] ?? [];
  const yValues = axis_values[req.axisY] ?? [];

  // Output sample axes: sweep across the axis's own node RANGE at the requested
  // resolution (default: the axis cardinality, so a coarse axis reproduces its
  // nodes exactly). A single-node axis yields one sample.
  const sampleAxis = (values: number[], n: number | undefined): number[] => {
    if (values.length <= 1) return values.length ? [values[0]!] : [0];
    const count = Math.max(2, Math.min(MAX_SLICE, n ?? values.length));
    const lo = axisMin(values);
    const hi = axisMax(values);
    const out = new Array<number>(count);
    for (let i = 0; i < count; i += 1) out[i] = lo + ((hi - lo) * i) / (count - 1);
    return out;
  };
  const xVals = sampleAxis(xValues, req.nx);
  const yVals = sampleAxis(yValues, req.ny);
  const nx = xVals.length;
  const ny = yVals.length;

  // Per-axis plan: held axes are resolved to a corner pair + blend ONCE; the two
  // swept axes are resolved per output cell inside the loop.
  const plans: AxisPlan[] = axes.map((axis, i) => {
    const values = axis_values[axis] ?? [];
    const cardinality = shape[i] ?? values.length ?? 1;
    if (axis === req.axisX) return { fixed: null, sweep: "x", cardinality };
    if (axis === req.axisY) return { fixed: null, sweep: "y", cardinality };
    if (cardinality <= 1 || values.length <= 1) {
      return { fixed: { i0: 0, i1: 0, t: 0 }, sweep: null, cardinality };
    }
    const raw = held[axis];
    const holdValue = Number.isFinite(raw) ? (raw as number) : axisMidpoint(values);
    return { fixed: bracket(values, holdValue), sweep: null, cardinality };
  });

  // Held axes' node lists are shared across the whole grid — build once.
  const heldNodes: Array<NodeWeight[] | null> = plans.map((plan) =>
    plan.sweep ? null : nodesForBracket(plan.fixed!),
  );

  const dim = axes.length;

  const interpAt = (
    getArr: (number | null)[] | null,
    xNodes: NodeWeight[],
    yNodes: NodeWeight[],
  ): number | null => {
    if (!getArr) return null;
    const perAxis: NodeWeight[][] = new Array(dim);
    for (let a = 0; a < dim; a += 1) {
      const plan = plans[a]!;
      if (plan.sweep === "x") perAxis[a] = xNodes;
      else if (plan.sweep === "y") perAxis[a] = yNodes;
      else perAxis[a] = heldNodes[a]!;
    }
    return interpCorners(getArr, perAxis, strides);
  };

  const z: (number | null)[] = new Array(nx * ny);
  const sig: (number | null)[] = new Array(nx * ny);
  let zMin = Infinity;
  let zMax = -Infinity;

  const yNodeCache = yVals.map((yv) =>
    nodesForBracket(yValues.length > 1 ? bracket(yValues, yv) : { i0: 0, i1: 0, t: 0 }),
  );
  const xNodeCache = xVals.map((xv) =>
    nodesForBracket(xValues.length > 1 ? bracket(xValues, xv) : { i0: 0, i1: 0, t: 0 }),
  );

  for (let jy = 0; jy < ny; jy += 1) {
    const yNodes = yNodeCache[jy]!;
    for (let ix = 0; ix < nx; ix += 1) {
      const xNodes = xNodeCache[ix]!;
      const cellIndex = jy * nx + ix;
      const zv = interpAt(zSource, xNodes, yNodes);
      z[cellIndex] = zv;
      sig[cellIndex] = interpAt(sigma, xNodes, yNodes);
      if (zv !== null) {
        if (zv < zMin) zMin = zv;
        if (zv > zMax) zMax = zv;
      }
    }
  }

  if (!Number.isFinite(zMin) || !Number.isFinite(zMax)) {
    zMin = 0;
    zMax = 1;
  }
  // avoid a degenerate colour scale on a perfectly flat slice
  if (Math.abs(zMax - zMin) < 1e-12) {
    zMin -= 0.5;
    zMax += 0.5;
  }

  return { xAxis: req.axisX, yAxis: req.axisY, xVals, yVals, z, sigma: sig, nx, ny, zMin, zMax };
}

/**
 * Default held map: every axis at its DENSEST evidence value (the bake's
 * `axis_densest` — where the solved runs actually live), clamped into the
 * axis's grid range; midpoint node fallback for axes without one (pre-fix
 * bakes). Callers seed their slider state from this, then let the user drag
 * the varying ones.
 *
 * Why not the midpoint node (FXC-9009 display-pipeline fix): on a fill
 * campaign the median-low grid node lands in zero-data corners — the default
 * slice rendered a low sliver while the entire evidence cloud floated above
 * it on stalks. The densest data value is the honest default slice.
 */
export function defaultHeld(field: GpFieldAvailable): Record<string, number> {
  const held: Record<string, number> = {};
  const densest = field.axis_densest ?? {};
  for (const axis of field.axes) {
    const values = field.axis_values[axis] ?? [];
    if (!values.length) continue;
    const d = densest[axis];
    held[axis] = typeof d === "number" && Number.isFinite(d)
      ? clamp(d, axisMin(values), axisMax(values))
      : axisMidpoint(values);
  }
  return held;
}

// ── shared surface-mesh math (DOM-free, WebGL-free — unit-testable) ──────────
// The single source of truth for turning a SliceResult into renderable geometry.
// Both TailorGpSurface (the single 3D surface) and TailorGpCorrelogram (the
// per-cell surfaces) build their mesh from this, at ONE world scale so their
// shared (2.6,2.2,2.6) fov-42 camera frames it: the grid spans [-width/2,+width/2]
// in X, [-depth/2,+depth/2] in Z, value → Y in [-height/2,+height/2]. Cells with
// a null/NaN z are dropped and any quad touching a dropped corner is skipped.

/** World-space extent the surface mesh is built into (matches the framing camera). */
export const SURFACE_LAYOUT = { width: 2.0, depth: 2.0, height: 1.0 } as const;

/** Viridis-ish colour ramp shared by the 3D surface + the correlogram cells. */
export function surfaceColorRgb(t: number): [number, number, number] {
  const stops: Array<[number, [number, number, number]]> = [
    [0, [37, 80, 154]],
    [0.24, [31, 130, 178]],
    [0.5, [26, 168, 132]],
    [0.74, [188, 201, 73]],
    [1, [226, 118, 43]],
  ];
  const clamped = Math.max(0, Math.min(1, t));
  for (let idx = 0; idx < stops.length - 1; idx += 1) {
    const [a, ca] = stops[idx]!;
    const [b, cb] = stops[idx + 1]!;
    if (clamped <= b) {
      const local = (clamped - a) / Math.max(1e-12, b - a);
      return [
        (ca[0] + (cb[0] - ca[0]) * local) / 255,
        (ca[1] + (cb[1] - ca[1]) * local) / 255,
        (ca[2] + (cb[2] - ca[2]) * local) / 255,
      ];
    }
  }
  return [226 / 255, 118 / 255, 43 / 255];
}

export interface SurfaceMeshData {
  /** flat xyz triples, world-scaled to SURFACE_LAYOUT */
  positions: number[];
  /** flat rgb triples (0..1) */
  colors: number[];
  /** per-vertex opacity (0..1): 1 where sigma is low (data-backed), fading to
   *  MIN_SIGMA_OPACITY where sigma is high (extrapolation). All 1 when the
   *  slice carries no sigma. The trust-surfaces "opacity = confidence" idiom. */
  alphas: number[];
  /** triangle indices into positions */
  indices: number[];
  vertexCount: number;
}

/** Ghost floor for the sigma-fade: the most-uncertain vertex keeps this much
 *  opacity so the surface shape stays readable while clearly unsupported. */
export const MIN_SIGMA_OPACITY = 0.16;

/** Optional mesh-build overrides. */
export interface SurfaceMeshOptions {
  /** Override the value→height scale (e.g. build the PRIOR mesh on the MEAN
   *  slice's scale so the two surfaces are height-comparable). */
  zMin?: number;
  zMax?: number;
  /** Ghost floor for the sigma fade (default MIN_SIGMA_OPACITY). */
  minOpacity?: number;
}

/** Map a slice value to its world Y height (shared with run-point placement). */
export function surfaceHeight(value: number, zMin: number, zMax: number): number {
  const zSpan = zMax - zMin || 1;
  return ((value - zMin) / zSpan - 0.5) * SURFACE_LAYOUT.height;
}

/**
 * Turn a SliceResult z-grid into a coloured triangle mesh. Pure — no THREE, no
 * DOM — so it unit-tests the geometry that actually gets rendered.
 *
 * Sigma-fade (FXC-9009): per-vertex `alphas` follow the slice's sigma —
 * normalised over the slice's own finite sigma range, alpha 1 at the lowest
 * sigma down to `minOpacity` at the highest. A slice with no (or flat) sigma
 * renders fully opaque.
 */
export function buildSurfaceMeshData(slice: SliceResult, opts: SurfaceMeshOptions = {}): SurfaceMeshData {
  const { nx, ny, z, sigma } = slice;
  const zMin = opts.zMin ?? slice.zMin;
  const zMax = opts.zMax ?? slice.zMax;
  const minOpacity = opts.minOpacity ?? MIN_SIGMA_OPACITY;
  const px = (i: number) => -SURFACE_LAYOUT.width / 2 + (i / (nx - 1 || 1)) * SURFACE_LAYOUT.width;
  const pz = (j: number) => -SURFACE_LAYOUT.depth / 2 + (j / (ny - 1 || 1)) * SURFACE_LAYOUT.depth;
  const py = (v: number) => surfaceHeight(v, zMin, zMax);

  // sigma range over the slice's finite cells (relative fade within the slice)
  let sigLo = Infinity;
  let sigHi = -Infinity;
  for (const s of sigma) {
    if (typeof s === "number" && Number.isFinite(s)) {
      if (s < sigLo) sigLo = s;
      if (s > sigHi) sigHi = s;
    }
  }
  const sigSpan = sigHi - sigLo;
  const fade = Number.isFinite(sigSpan) && sigSpan > 1e-12;

  const positions: number[] = [];
  const colors: number[] = [];
  const alphas: number[] = [];
  const vIndex = new Int32Array(nx * ny).fill(-1);
  let vCount = 0;
  for (let j = 0; j < ny; j += 1) {
    for (let i = 0; i < nx; i += 1) {
      const idx = j * nx + i;
      const v = z[idx];
      if (v == null || !Number.isFinite(v)) continue;
      vIndex[idx] = vCount;
      positions.push(px(i), py(v), pz(j));
      const [r, g, b] = surfaceColorRgb((v - zMin) / (zMax - zMin || 1));
      colors.push(r, g, b);
      const s = sigma[idx];
      if (fade && typeof s === "number" && Number.isFinite(s)) {
        const t = (s - sigLo) / sigSpan;
        alphas.push(1 - t * (1 - minOpacity));
      } else {
        alphas.push(1);
      }
      vCount += 1;
    }
  }
  const indices: number[] = [];
  if (vCount >= 3) {
    for (let j = 0; j < ny - 1; j += 1) {
      for (let i = 0; i < nx - 1; i += 1) {
        const a = vIndex[j * nx + i] ?? -1;
        const b = vIndex[j * nx + i + 1] ?? -1;
        const c = vIndex[(j + 1) * nx + i] ?? -1;
        const d = vIndex[(j + 1) * nx + i + 1] ?? -1;
        if (a < 0 || b < 0 || c < 0 || d < 0) continue;
        indices.push(a, c, b, b, c, d);
      }
    }
  }
  return { positions, colors, alphas, indices, vertexCount: vCount };
}

/**
 * 2D convex hull (Andrew's monotone chain) of the projected in-slice runs —
 * the data-support boundary: beyond this outline the surface is pure model.
 * Returns the hull vertices in counter-clockwise order; [] when fewer than 3
 * distinct points exist (no meaningful hull).
 */
export function convexHull2d(points: Array<{ x: number; y: number }>): Array<{ x: number; y: number }> {
  const unique = new Map<string, { x: number; y: number }>();
  for (const p of points) {
    if (Number.isFinite(p.x) && Number.isFinite(p.y)) unique.set(`${p.x}|${p.y}`, { x: p.x, y: p.y });
  }
  const pts = [...unique.values()].sort((a, b) => a.x - b.x || a.y - b.y);
  if (pts.length < 3) return [];
  const cross = (o: { x: number; y: number }, a: { x: number; y: number }, b: { x: number; y: number }) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower: Array<{ x: number; y: number }> = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2]!, lower[lower.length - 1]!, p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: Array<{ x: number; y: number }> = [];
  for (let i = pts.length - 1; i >= 0; i -= 1) {
    const p = pts[i]!;
    while (upper.length >= 2 && cross(upper[upper.length - 2]!, upper[upper.length - 1]!, p) <= 0) upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  const hull = [...lower, ...upper];
  return hull.length >= 3 ? hull : [];
}

// ── point evaluation + evidence transport (FXC-9009 item 4) ──────────────────

/**
 * Multilinear interpolation of the field MEAN at ONE full condition — the same
 * interpolator sliceGpField sweeps per cell, applied to a single point. Missing
 * axes default to their midpoint node; values clamp to the axis range;
 * cardinality-1 (geometry) axes contribute no interpolation.
 */
const fieldStridesCache = new WeakMap<GpFieldAvailable, number[]>();
function stridesForField(field: GpFieldAvailable): number[] {
  const hit = fieldStridesCache.get(field);
  if (hit) return hit;
  const strides = cOrderStrides(field.shape);
  fieldStridesCache.set(field, strides);
  return strides;
}

export function evalGpField(field: GpFieldAvailable, condition: Record<string, number>): number | null {
  const { axes, shape, axis_values, mean } = field;
  const strides = stridesForField(field);
  const perAxis: NodeWeight[][] = axes.map((axis, i) => {
    const values = axis_values[axis] ?? [];
    const cardinality = shape[i] ?? values.length ?? 1;
    if (cardinality <= 1 || values.length <= 1) return [{ idx: 0, w: 1 }];
    const raw = condition[axis];
    const value = Number.isFinite(raw) ? (raw as number) : axisMidpoint(values);
    return nodesForBracket(bracket(values, value));
  });
  return interpCorners(mean, perAxis, strides);
}

/** Point-evidence rendering mode for the GP views (mirrors the explorer's
 *  Raw/Compatible/Corrected tabs, but powered by the GP itself). */
export type GpEvidenceMode = "raw" | "compatible" | "corrected";

/** A projected run the evidence modes operate on. `row` carries the run's full
 *  condition (axis values) — needed to judge compatibility / transport. */
export interface GpEvidenceRun {
  x: number;
  y: number;
  value: number;
  row?: Record<string, unknown>;
}

/** Compatible-mode tolerance: |own − held| ≤ fraction × axis span. */
export const COMPATIBLE_SPAN_FRACTION = 0.15;

function numericCell(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) return Number(value);
  return null;
}

/**
 * Apply a point-evidence mode to projected runs for a (axisX, axisY) plane at
 * the shared `held` slice. The SURFACE is never touched — only the run POINTS:
 *   raw        — every run at its measured value (unchanged);
 *   compatible — keep only runs whose OFF-PLANE varying-axis values sit within
 *                COMPATIBLE_SPAN_FRACTION of the held values (runs lacking an
 *                axis value are kept — they can't be judged);
 *   corrected  — the classic Tailor transport onto the held slice:
 *                  corrected = raw + GP(x, y, held) − GP(x, y, run's own values)
 *                so points collapse onto/near the displayed surface. Runs the
 *                GP can't evaluate (null) keep their raw value.
 */
export function applyEvidenceMode<T extends GpEvidenceRun>(
  field: GpFieldAvailable,
  runs: T[],
  opts: {
    axisX: string;
    axisY: string;
    held: Record<string, number>;
    mode: GpEvidenceMode;
    toleranceFraction?: number;
    /** Optional cross-call cache of GP(run's OWN full condition), keyed by the
     *  run's `row` object (FXC-9009 perf). The corrected transport's `atOwn`
     *  eval is plane-independent for a run whose row carries its own numeric
     *  value on every varying axis — exactly what continuous fill campaigns
     *  produce — so the high-D correlogram can share ONE eval per row across
     *  all of its ~k² pair cells instead of re-walking the interpolation
     *  hypercube per cell. Only consulted/filled for such complete rows;
     *  incomplete rows keep the exact per-plane fallback semantics. */
    ownEvals?: Map<Record<string, unknown>, number | null>;
  },
): T[] {
  if (opts.mode === "raw") return runs;
  const offPlane = field.axes_varying.filter((a) => a !== opts.axisX && a !== opts.axisY);
  if (!offPlane.length) return runs;

  const heldFor = (axis: string): number => {
    const values = field.axis_values[axis] ?? [];
    const v = opts.held[axis];
    return Number.isFinite(v) ? (v as number) : axisMidpoint(values);
  };

  if (opts.mode === "compatible") {
    const tolFrac = opts.toleranceFraction ?? COMPATIBLE_SPAN_FRACTION;
    return runs.filter((run) => {
      for (const axis of offPlane) {
        const own = numericCell(run.row?.[axis]);
        if (own === null) continue; // can't judge → keep
        const values = field.axis_values[axis] ?? [];
        const span = Math.abs((values[values.length - 1] ?? 1) - (values[0] ?? 0)) || 1;
        if (Math.abs(own - heldFor(axis)) > tolFrac * span) return false;
      }
      return true;
    });
  }

  // corrected — transport every run onto the held slice
  return runs.map((run) => {
    const target: Record<string, number> = { [opts.axisX]: run.x, [opts.axisY]: run.y };
    const source: Record<string, number> = { [opts.axisX]: run.x, [opts.axisY]: run.y };
    let moved = false;
    // `source` equals the run's OWN full condition (→ cacheable across planes)
    // only when every off-plane axis has an own value AND the plane coords came
    // from the row itself (projectRuns guarantees this; guard anyway).
    let ownComplete =
      !!run.row &&
      numericCell(run.row[opts.axisX]) === run.x &&
      numericCell(run.row[opts.axisY]) === run.y;
    for (const axis of offPlane) {
      const held = heldFor(axis);
      target[axis] = held;
      const own = numericCell(run.row?.[axis]);
      source[axis] = own ?? held;
      if (own === null) ownComplete = false;
      if (own !== null && own !== held) moved = true;
    }
    if (!moved) return run; // already on the slice — transport is identically 0
    const atHeld = evalGpField(field, target);
    let atOwn: number | null;
    const cache = opts.ownEvals;
    if (cache && ownComplete && run.row) {
      const hit = cache.get(run.row);
      if (hit !== undefined) {
        atOwn = hit;
      } else {
        atOwn = evalGpField(field, source);
        cache.set(run.row, atOwn);
      }
    } else {
      atOwn = evalGpField(field, source);
    }
    if (atHeld === null || atOwn === null) return run;
    return { ...run, value: run.value + (atHeld - atOwn) };
  });
}

// ── shared colour-by ramp (blue→teal→green→amber→red) ────────────────────────
// One ramp for every "colour points by axis" surface: exported here so the 2D
// TrustLinePanel scatter, the 3D GP surface spheres, and any colourbar legend
// all read the same scale. Sequential, colour-blind-tolerant at the ends.
export const RAMP_STOPS: ReadonlyArray<readonly [number, number, number]> = [
  [46, 74, 158],   // deep blue
  [38, 139, 176],  // teal
  [63, 167, 110],  // green
  [224, 176, 54],  // amber
  [201, 74, 56],   // red
];

/** Ramp colour as 0..1 rgb components (three.js instance colours). t in [0,1]. */
export function rampColorRgb(t: number): [number, number, number] {
  const c = Math.max(0, Math.min(1, t)) * (RAMP_STOPS.length - 1);
  const i = Math.min(RAMP_STOPS.length - 2, Math.floor(c));
  const f = c - i;
  const a = RAMP_STOPS[i] ?? RAMP_STOPS[0]!;
  const b = RAMP_STOPS[i + 1] ?? a;
  const mix = (k: 0 | 1 | 2) => (a[k] + (b[k] - a[k]) * f) / 255;
  return [mix(0), mix(1), mix(2)];
}

/** Ramp colour as a CSS rgb() string (SVG/DOM consumers). t in [0,1]. */
export function rampColorCss(t: number): string {
  const [r, g, b] = rampColorRgb(t);
  return `rgb(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)})`;
}
